import React, { type ReactNode } from 'react';

/**
 * Generic expanded-row detail container. Was a two-mode system (a wide grid
 * vs. a narrow tab strip mounting only the active panel) — replaced
 * (2026-09-23, Spencer QA) with a single always-all-panels-mounted CSS grid
 * that wraps via `repeat(auto-fit, minmax(...))`: no breakpoint, no tabs, no
 * "which panel is the default" question. Every panel is always visible; the
 * grid just reflows to fewer columns (down to 1) as the window narrows.
 *
 * Every panel mounting unconditionally means `ContactTimeline`'s Middleman
 * GET and `ContactIntelPanel`'s Sonnet generation both fire on every row
 * expansion, not gated behind a tab click — this was already true for
 * anyone on a wide-enough window under the old system, so this just makes
 * it universal rather than introducing new behavior.
 */

export interface FlowDetailPanel {
  key: string;
  label: string;
  content: ReactNode;
  /**
   * Optional element rendered at the right end of the label row (e.g. the
   * Contact Intelligence panel's prompt-settings gear, U5). Omitted panels'
   * DOM is unchanged apart from the label row's flex wrapper class.
   */
  headerAction?: ReactNode;
}

/**
 * The shared panel-card look. `PANEL_CARD_CLASS` is the grid cell (height-
 * capped, scrolls internally); `LEAD_PANEL_CARD_CLASS` is the Signal card's
 * OWN grid cell (qa-batch4-plan.md D-A, reversing QA batch 2's "full-width
 * strip above the grid") — the same card minus the `max-h-[380px]
 * overflow-y-auto` pair, because the Signal card's inline archive form must
 * never scroll inside its own card. It doubles as `LeadPanelCard`'s
 * standalone surface (below) for `SignalDetail`'s pre-loaded branches.
 */
const PANEL_CARD_SURFACE_CLASS =
  'bg-white dark:bg-nightshade-900 border border-secondary dark:border-nightshade-700 rounded-md p-3';
export const PANEL_CARD_CLASS = `min-w-0 max-h-[380px] overflow-y-auto ${PANEL_CARD_SURFACE_CLASS}`;
export const LEAD_PANEL_CARD_CLASS = `min-w-0 ${PANEL_CARD_SURFACE_CLASS}`;

const PANEL_LABEL_ROW_CLASS =
  'flex items-center justify-between flex-wrap gap-y-1 text-[10.5px] uppercase tracking-wide text-content-tertiary dark:text-nightshade-500 font-medium mb-1.5';

/**
 * The full-width standalone Signal card for `SignalDetail`'s FOUR
 * pre-loaded branches (no contact / loading / error / no mirror row) — F5.
 * Those branches never reach `FlowDetail`'s panel grid (there is no Contact
 * Info to sit beside yet), so they render this directly instead of going
 * through `ContactDetail`'s `leadPanel` prop, followed by a one-line hint.
 * The LOADED branch instead hands `leadPanel` to `ContactDetail` ->
 * `FlowDetail`, which renders it as the FIRST cell of the panel grid
 * (qa-batch4-plan.md D-A) rather than through this component — the two
 * surfaces share `LEAD_PANEL_CARD_CLASS` (this one adds `mb-2.5` before the
 * hint line) but are otherwise the same markup, pinned by `FlowDetail.test.tsx`.
 */
export function LeadPanelCard({ panel }: { panel: FlowDetailPanel }) {
  return (
    <div data-testid="flow-detail-lead-panel" className={`${LEAD_PANEL_CARD_CLASS} mb-2.5`}>
      <div className={PANEL_LABEL_ROW_CLASS}>
        <span>{panel.label}</span>
        {panel.headerAction}
      </div>
      {panel.content}
    </div>
  );
}

export function FlowDetail({
  panels, footer, topStrip, leadPanel,
}: {
  panels: FlowDetailPanel[];
  footer?: ReactNode;
  /** Full-width bar rendered above the panel grid (e.g. ContactDetail's Terra Actions strip). Renders nothing when omitted/null. */
  topStrip?: ReactNode;
  /**
   * The Signal card's cell (qa-batch4-plan.md D-A) — rendered as the FIRST
   * cell of the panel grid itself, not a full-width strip above it: on a
   * wide window it sits to the LEFT of Contact Info, and on a narrow one it
   * stacks directly ABOVE it, following the same
   * `repeat(auto-fit, minmax(400px, 1fr))` rule every other cell already
   * follows. Omitted → markup identical to before (no extra cell).
   */
  leadPanel?: FlowDetailPanel;
}) {
  return (
    <div className="p-3">
      {topStrip}
      <div
        className="w-full grid gap-2.5"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))' }}
      >
        {leadPanel && (
          <div key={leadPanel.key} data-testid="flow-detail-lead-panel" className={LEAD_PANEL_CARD_CLASS}>
            <div className={PANEL_LABEL_ROW_CLASS}>
              <span>{leadPanel.label}</span>
              {leadPanel.headerAction}
            </div>
            {leadPanel.content}
          </div>
        )}
        {panels.map(p => (
          <div key={p.key} className={PANEL_CARD_CLASS}>
            <div className={PANEL_LABEL_ROW_CLASS}>
              <span>{p.label}</span>
              {p.headerAction}
            </div>
            {p.content}
          </div>
        ))}
      </div>
      {footer && <div className="w-full mt-2.5">{footer}</div>}
    </div>
  );
}

const DETAIL_KV_LABEL_WIDTH = { default: 'w-[104px]', narrow: 'w-[72px]' } as const;

/**
 * Compact key/value line used inside detail panels (prototype's .kv).
 * `labelWidth="narrow"` (72 px) and `className` (appended to the root) exist
 * for the Signal card's 2-column grid (qa-batch5-plan.md D-C); omitted, the
 * markup is byte-identical to before (Contact Info).
 */
export function DetailKV({ k, v, href, labelWidth = 'default', className }: {
  k: string;
  v: ReactNode;
  href?: string;
  labelWidth?: keyof typeof DETAIL_KV_LABEL_WIDTH;
  className?: string;
}) {
  const val = v == null || v === '' ? <span className="text-content-faint dark:text-nightshade-600">—</span> : v;
  return (
    <div className={`flex py-[3px] border-b border-primary/40 dark:border-nightshade-800 text-[12px] last:border-b-0${className ? ` ${className}` : ''}`}>
      <span className={`${DETAIL_KV_LABEL_WIDTH[labelWidth]} shrink-0 text-content-tertiary dark:text-nightshade-500`}>{k}</span>
      <span className="font-medium text-content-primary dark:text-nightshade-100 min-w-0 truncate">
        {href && v ? (
          <a href={href} target="_blank" rel="noreferrer" className="text-content-action dark:text-blueberry-400 hover:underline">{val}</a>
        ) : val}
      </span>
    </div>
  );
}
